import React, { useState } from "react";
import cn from "classnames";
import styles from "./style.scss";
import {
  ArticleReference,
  CompanyListTableReference,
  FinancialChartReference,
  FinancialTableReference,
  MacroChartReference,
  MacroIrChartReference,
  Reference as ReferenceType
} from "models/AiResearch";
import { formatDate } from "utils/helper/day";
import { Graph } from "./Graph";
import ArrowDown from "images/icon/icon_24x24_arrow_down.svg";
import ArrowUp from "images/icon/icon_24x24_arrow_up.svg";
import { trackAction } from "utils/helper/analytics";

const isArticleReference = (
  reference: ReferenceType
): reference is
  | ArticleReference
  | FinancialTableReference
  | CompanyListTableReference =>
  reference.dataType === "article_chunk" ||
  reference.dataType === "financial_table" ||
  reference.dataType === "company_list_table";

const isChartReference = (
  reference: ReferenceType
): reference is
  | FinancialChartReference
  | MacroIrChartReference
  | MacroChartReference =>
  reference.dataType === "financial_chart" ||
  reference.dataType === "macro_chart" ||
  reference.dataType === "macro_ir_chart";

interface Props {
  references: ReferenceType[];
  onShowReference: (referenceId: number) => void;
}

export const References: React.FC<Props> = ({
  references,
  onShowReference
}) => {
  const [isOpen, setIsOpen] = useState(false);

  // 記事・テーブルデータ
  const articleTableSource = references
    .filter(isArticleReference)
    .sort((a, b) => a.citationNumber - b.citationNumber);

  // グラフデータ
  const graphSource = references
    .filter(isChartReference)
    .sort((a, b) => a.citationNumber - b.citationNumber);

  return (
    <div className={styles.container}>
      <div
        className={cn(styles.title, {
          [styles.expended]: isOpen
        })}
        onClick={() => {
          trackAction("clickReferenceExpandBtn", {
            isOpen: !isOpen
          });
          setIsOpen(!isOpen);
        }}
      >
        <div className={styles.left}>
          <div className={styles.text}>情報ソース</div>
          <div className={styles.count}>{references.length}件</div>
        </div>
        <img
          className={styles.icon}
          src={isOpen ? ArrowUp : ArrowDown}
          alt="expand"
        />
      </div>
      {isOpen && (
        <div className={styles.content}>
          {articleTableSource.map((data, index) => (
            <div className={styles.mediaItem} key={index}>
              <div className={styles.titleRow}>
                <span
                  className={styles.seriesNo}
                  onClick={() => onShowReference(data.citationNumber)}
                  data-reference-button="true"
                >
                  {data.citationNumber}
                </span>
                <span
                  className={styles.text}
                  onClick={() => onShowReference(data.citationNumber)}
                  data-reference-button="true"
                >
                  {data.dataType === "article_chunk"
                    ? data.data.title
                    : data.data.content.name}
                </span>
              </div>
              {/* 記事の場合のみ公開日と媒体名を表示 */}
              {data.dataType === "article_chunk" && (
                <div className={styles.articleMediaInfo}>
                  <div className={styles.mediaDate}>
                    {formatDate(String(data.data.publishDate))}
                  </div>
                  <div className={styles.mediaName}>{data.data.mediaName}</div>
                </div>
              )}
            </div>
          ))}
          {graphSource.length > 0 && (
            <section className={styles.graphSection}>
              <div className={styles.graphTitle}>関連データ</div>
              <div className={styles.graphList}>
                {graphSource.map((reference, index) => (
                  <div className={styles.graphItem} key={index}>
                    <Graph
                      reference={reference}
                      onClick={() => onShowReference(reference.citationNumber)}
                    />
                    <div className={styles.graphTitleRow}>
                      <span
                        className={styles.seriesNo}
                        onClick={() =>
                          onShowReference(reference.citationNumber)
                        }
                        data-reference-button="true"
                      >
                        {reference.citationNumber}
                      </span>
                      <span
                        className={styles.graphTitleText}
                        onClick={() =>
                          onShowReference(reference.citationNumber)
                        }
                        data-reference-button="true"
                      >
                        {reference.data.meta.name}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
};
